var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useCupFamilyOptions } from "hooks/use-cup-family-options";
import { useCupMountingOptions } from "hooks/use-cup-mounting-options";
import { useCupChoice } from "hooks/use-cup-choice";
import { useCupOptions } from "hooks/use-cup-options";
import { useCupPumpOptions } from "hooks/use-cup-pump-options";
import { usePumpFamilySelection } from "hooks/use-pump-family-selection";
import { useOpenFamilies } from "hooks/use-open-families";
import { actions, selectors, useAppDispatch, useAppSelector } from "store";
import { noCupImage, noPumpImage } from "3d/constants/common";
import { useCupTechnologyOptions } from "hooks/use-cup-technology-options";
import { ComponentSelect } from "ui/components/panels/canvas/node-components/component-select/component-select";
import { FamilyAccordionSelect } from "ui/components/panels/canvas/node-components/component-select/family-accordion-select";
import { CupChoiceSelect } from "ui/components/panels/canvas/node-components/component-select/cup-choice-select";
import { PumpChoiceSelect } from "ui/components/panels/canvas/node-components/component-select/pump-choice-select";
export var useComponentSelect = function (nodes, scrollIntoPosition, selectedSection, onFamilySelected) {
    var dispatch = useAppDispatch();
    var t = useTranslation().t;
    var nodeIds = useMemo(function () { return nodes.map(function (_a) {
        var id = _a.id;
        return id;
    }); }, [nodes]);
    var _a = useCupFamilyOptions(), cupFamilyOptions = _a.cupFamilyOptions, cupFamilyDropdownImages = _a.cupFamilyDropdownImages;
    var selectedCups = useAppSelector(selectors.selectSelectedCups(nodeIds));
    var familiesOfSelectedCups = Array.from(new Set(selectedCups.map(function (cup) { return cup === null || cup === void 0 ? void 0 : cup.cupFamily; }).filter(Boolean)));
    var _b = __read(useOpenFamilies(familiesOfSelectedCups), 2), selectedCupFamilies = _b[0], setSelectedCupFamilies = _b[1];
    var _c = useCupOptions(nodes, selectedCupFamilies), cupOptions = _c.cupOptions, selectedCupOptions = _c.selectedCupOptions, selectedCupImage = _c.selectedCupImage;
    var commitCupToNodes = useCallback(function (cup) { return dispatch(actions.selectCup({ nodeIds: nodeIds, value: cup })); }, [dispatch, nodeIds]);
    var _d = useCupChoice(selectedCupFamilies, selectedCupOptions, commitCupToNodes, nodeIds), hasCupChoices = _d.hasCupChoices, cupChoice = _d.cupChoice, pickCupFamily = _d.pickCupFamily, commitCup = _d.commitCup;
    var _e = useCupMountingOptions(nodeIds), cupMountingOptions = _e.cupMountingOptions, selectedCupMountingOptions = _e.selectedCupMountingOptions;
    var _f = useCupPumpOptions(nodeIds), pumpOptions = _f.pumpOptions, selectedPumpOptions = _f.selectedPumpOptions;
    var commitPump = useCallback(function (pump) {
        return dispatch(actions.selectDecentralPump({ nodeIds: nodeIds, value: pump }));
    }, [dispatch, nodeIds]);
    var _g = usePumpFamilySelection(pumpOptions, selectedPumpOptions, commitPump, nodeIds), pumpFamilyOptions = _g.pumpFamilyOptions, pumpFamilyImages = _g.pumpFamilyImages, selectedPumpFamilies = _g.selectedPumpFamilies, onPickPumpFamily = _g.onPickPumpFamily, pumpItemOptions = _g.pumpItemOptions, pumpChoice = _g.pumpChoice, feedPressureUnit = _g.feedPressureUnit, onSelectPump = _g.onSelectPump;
    var _h = useCupTechnologyOptions(nodeIds), cupTechnologyOptions = _h.cupTechnologyOptions, selectedCupTechnologyOptions = _h.selectedCupTechnologyOptions;
    var onSelectCupFamily = useCallback(function (cupFamily) {
        setSelectedCupFamilies([cupFamily]);
        pickCupFamily(cupFamily);
        onFamilySelected === null || onFamilySelected === void 0 ? void 0 : onFamilySelected();
    }, [onFamilySelected, setSelectedCupFamilies, pickCupFamily]);
    var onSelectCup = useCallback(function (cup) {
        var selectedCupOption = cupOptions.find(function (option) { return option.value === cup; });
        if (selectedCupOption === null || selectedCupOption === void 0 ? void 0 : selectedCupOption.cupFamily) {
            setSelectedCupFamilies([selectedCupOption.cupFamily]);
        }
        commitCup(cup);
    }, [cupOptions, commitCup, setSelectedCupFamilies]);
    var onSelectCupMounting = useCallback(function (cupMounting) {
        dispatch(actions.selectCupMounting({
            nodeIds: nodeIds,
            value: cupMounting,
        }));
    }, [dispatch, nodeIds]);
    var onSelectCupTechnology = useCallback(function (cupTechnology) {
        dispatch(actions.selectCupTechnology({ nodeIds: nodeIds, value: cupTechnology }));
    }, [dispatch, nodeIds]);
    var component = useMemo(function () {
        var _a;
        switch (selectedSection) {
            case "family":
                return (_jsx(ComponentSelect, { labels: cupFamilyOptions.map(function (option) { return option; }), values: cupFamilyOptions.map(function (option) { return option; }), images: cupFamilyDropdownImages, selectedValues: selectedCupFamilies, onValueChange: onSelectCupFamily, scrollIntoPosition: scrollIntoPosition }, selectedSection));
            case "cup-combined":
                return (_jsx(FamilyAccordionSelect, { familyHeader: t("edit_grip_position.select_cup_family"), itemHeader: t("edit_grip_position.select_cup_model"), noItemImage: noCupImage, familyOptions: cupFamilyOptions, familyImages: cupFamilyDropdownImages, selectedFamilies: selectedCupFamilies, onSelectFamily: onSelectCupFamily, itemOptions: cupOptions, selectedItemValues: selectedCupOptions.map(function (cup) { return cup.value; }), selectedItemImage: selectedCupImage, onSelectItem: onSelectCup, itemContent: hasCupChoices ? (_jsx(CupChoiceSelect, { cupChoice: cupChoice })) : undefined, scrollIntoPosition: scrollIntoPosition }, selectedSection));
            case "cup":
                return (_jsx(ComponentSelect, { labels: cupOptions.map(function (option) { return option.label; }), values: cupOptions.map(function (option) { return option.value; }), images: cupOptions.map(function (option) { return option.image; }), selectedValues: selectedCupOptions.map(function (cup) { return cup.value; }), onValueChange: onSelectCup, scrollIntoPosition: scrollIntoPosition }, selectedSection));
            case "technology":
                return (_jsx(ComponentSelect, { labels: cupTechnologyOptions.map(function (option) { return option.label; }), values: cupTechnologyOptions.map(function (option) { return option.value; }), groups: cupTechnologyOptions.map(function (option) {
                        return option.type === "None" ? undefined : option.type;
                    }), selectedValues: selectedCupTechnologyOptions.map(function (tech) { return tech.value; }), onValueChange: onSelectCupTechnology, scrollIntoPosition: scrollIntoPosition }, selectedSection));
            case "mounting":
                return (_jsx(ComponentSelect, { labels: cupMountingOptions.map(function (option) { return option.label; }), values: cupMountingOptions.map(function (option) { return option.value; }), 
                    /* The rendered thumbnails are generated (see README "Component
                     * thumbnails") but held back until the 3D artist's own product
                     * images are in place. Re-enable to show them:
                     * images={cupMountingOptions.map(getCupMountingImage)} */
                    selectedValues: selectedCupMountingOptions.map(function (mounting) { return mounting.value; }), onValueChange: onSelectCupMounting, scrollIntoPosition: scrollIntoPosition }, selectedSection));
            case "pump":
                return (_jsx(FamilyAccordionSelect, { familyHeader: t("sidebar.edit_pump.select_pump_family"), itemHeader: t("sidebar.edit_pump.select_pump_model"), noItemImage: noPumpImage, familyOptions: pumpFamilyOptions, familyImages: pumpFamilyImages, selectedFamilies: selectedPumpFamilies, onSelectFamily: onPickPumpFamily, itemOptions: pumpItemOptions, selectedItemValues: selectedPumpOptions.map(function (pump) { return pump.value; }), selectedItemImage: (_a = selectedPumpOptions[0]) === null || _a === void 0 ? void 0 : _a.image, onSelectItem: onSelectPump, itemContent: _jsx(PumpChoiceSelect, { pumpChoice: pumpChoice, feedPressureUnit: feedPressureUnit }), scrollIntoPosition: scrollIntoPosition }, selectedSection));
            default:
                return undefined;
        }
    }, [
        selectedSection,
        t,
        cupFamilyOptions,
        cupFamilyDropdownImages,
        selectedCupFamilies,
        onSelectCupFamily,
        scrollIntoPosition,
        cupOptions,
        selectedCupOptions,
        selectedCupImage,
        onSelectCup,
        hasCupChoices,
        cupChoice,
        cupTechnologyOptions,
        selectedCupTechnologyOptions,
        onSelectCupTechnology,
        cupMountingOptions,
        selectedCupMountingOptions,
        onSelectCupMounting,
        feedPressureUnit,
        pumpFamilyOptions,
        pumpFamilyImages,
        selectedPumpFamilies,
        onPickPumpFamily,
        pumpItemOptions,
        pumpChoice,
        selectedPumpOptions,
        onSelectPump,
    ]);
    return {
        component: component,
        selectedCupFamilies: selectedCupFamilies,
        cupFamilyOptions: cupFamilyOptions,
        cupFamilyDropdownImages: cupFamilyDropdownImages,
        cupOptions: cupOptions,
        selectedCupOptions: selectedCupOptions,
        onSelectCupFamily: onSelectCupFamily,
        onSelectCup: onSelectCup,
        hasCupChoices: hasCupChoices,
        cupChoice: cupChoice,
        selectedCupMountingOptions: selectedCupMountingOptions,
        selectedPumpOptions: selectedPumpOptions,
        pumpFamilyOptions: pumpFamilyOptions,
        pumpFamilyImages: pumpFamilyImages,
        selectedPumpFamilies: selectedPumpFamilies,
        onPickPumpFamily: onPickPumpFamily,
        pumpItemOptions: pumpItemOptions,
        pumpChoice: pumpChoice,
        feedPressureUnit: feedPressureUnit,
        onSelectPump: onSelectPump,
        selectedCupTechnologyOptions: selectedCupTechnologyOptions,
        cupTechnologyOptions: cupTechnologyOptions,
    };
};
